import { useEffect, useState } from "react";
import { DropdownButton, PixelPlaceholderLoader, Tooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";

import { ADD_FIELD } from "../const";
import { FieldAlreadyInUseTooltipContent } from "../components/FieldAlreadyInUseTooltipContent";
import { InvalidDataTypeTooltipContent } from "../components/InvalidDataTypeTooltipContent";
import { getIntegrationNameFromUrl } from "../getIntegrationNameFromUrl";
import { chopTextOverTheLimit, createPlacement } from "../utils";
import type { CrmFieldType } from "../types";
import {
    StyledCellContainer,
    StyledDropdownWrapper,
    StyledDropdown,
    StyledLoader,
    StyledCrmDropdownItem,
    StyledCrmDropdownItemGroup,
} from "../styles";

import {
    ALL_FIELDS_GROUP_LABEL,
    CRM_CELL,
    CRM_CELL_PLACEHOLDER,
    CRM_EXPORT_DROPDOWN_HEIGHT,
    CRM_EXPORT_DROPDOWN_WIDTH,
    CRM_FIELD,
    MANDATORY_FIELDS_GROUP_LABEL,
    NUMBER_OF_ITEMS_IN_CRM_DD,
    OVER_THE_LIMIT_LENGTH,
} from "./constants";
import type { CrmCellPropsType } from "./types";

export const CrmCell = ({
    onClick,
    handleToggle,
    focusIdx,
    index,
    field,
    selectedCrmField,
    options,
    isLoading,
    columns,
    isColumnsLoading,
    isPreDefined,
    selectedCrmFields,
    tableLength,
    exportName,
    groupByRequired = true,
    valueType,
}: CrmCellPropsType) => {
    const [isFocused, setIsFocused] = useState(false);
    const { placement } = createPlacement(tableLength, index);
    const { integrationName } = getIntegrationNameFromUrl();
    const crmName = integrationName ? getCrmName(integrationName as Integration) : "CRM";

    const hasRequiredField = groupByRequired && options.some((option) => option.isRequired);

    useEffect(() => {
        const isFocusing = !selectedCrmField && focusIdx === index;
        setIsFocused(isFocusing);
    }, [selectedCrmField, index, focusIdx]);

    const renderDropdownItem = ({
        label,
        name,
        isApplicable,
        dataType,
        pickListItems,
        compatibleCrmTypes,
    }: CrmFieldType) => {
        const isSelected = selectedCrmField === name;
        const isFieldInUse = !isSelected && selectedCrmFields.includes(name);
        const isInvalidDataType = !isPreDefined && isApplicable === false;
        const isInvalidPickList =
            isPreDefined && dataType === "Picklist" && (!pickListItems || Object.keys(pickListItems).length === 0);
        const isDisabled = !isSelected && (isInvalidDataType || isFieldInUse || isInvalidPickList);

        const tooltipContent = isInvalidDataType ? (
            <InvalidDataTypeTooltipContent
                valueType={valueType}
                compatibleCrmTypes={compatibleCrmTypes}
                crmName={crmName}
            />
        ) : isFieldInUse ? (
            <FieldAlreadyInUseTooltipContent />
        ) : undefined;

        const itemKey = `${CRM_FIELD}-${name}`;
        const dropdownItemProps = {
            text: label,
            id: name,
            disabled: isDisabled,
            extraPadding: hasRequiredField,
            selected: isSelected,
        };

        const dropdownItem = <StyledCrmDropdownItem {...dropdownItemProps}>{label}</StyledCrmDropdownItem>;

        if (!tooltipContent) {
            return (
                <StyledCrmDropdownItem
                    key={itemKey}
                    {...dropdownItemProps}
                >
                    {label}
                </StyledCrmDropdownItem>
            );
        }

        return (
            <Tooltip
                key={itemKey}
                placement="top"
                content={tooltipContent}
            >
                <Box width="100%">{dropdownItem}</Box>
            </Tooltip>
        );
    };

    const renderDropdownItems = () => {
        const currentSelectedOption = columns.find((item) => item.name === selectedCrmField);

        const allOptions = [
            <Tooltip
                placement="top"
                content={selectedCrmField}
                enabled={selectedCrmField.length > OVER_THE_LIMIT_LENGTH}
                key={`${CRM_CELL}-button`}
            >
                <DropdownButton disabled={isColumnsLoading}>
                    {isColumnsLoading ? (
                        <PixelPlaceholderLoader
                            width="98%"
                            height={20}
                        />
                    ) : (
                        chopTextOverTheLimit(currentSelectedOption?.label || selectedCrmField, OVER_THE_LIMIT_LENGTH)
                    )}
                </DropdownButton>
            </Tooltip>,
        ];

        const renderLoadingPlaceholder = () => {
            const preloader = [];

            for (let index = 0; index < NUMBER_OF_ITEMS_IN_CRM_DD; index++) {
                preloader.push(
                    <StyledLoader
                        id="loader"
                        width="98%"
                        height={20}
                    />,
                );
            }

            return preloader;
        };

        if (isLoading) {
            return allOptions.concat(<>{renderLoadingPlaceholder()}</>);
        }

        const filteredOptions = options.filter((option) => !!option.label);

        if (groupByRequired) {
            const requiredOptions = filteredOptions.filter((item) => item.isRequired);
            const nonRequiredOptions = filteredOptions.filter((item) => !item.isRequired);

            if (requiredOptions.length) {
                allOptions.push(
                    <StyledCrmDropdownItemGroup
                        key="required-fields-group"
                        iconName="category"
                        iconSize="sm"
                        preventDefault
                    >
                        {translate(MANDATORY_FIELDS_GROUP_LABEL)}
                    </StyledCrmDropdownItemGroup>,
                );

                allOptions.push(...requiredOptions.map(renderDropdownItem));

                allOptions.push(
                    <StyledCrmDropdownItemGroup
                        key="all-fields-group"
                        iconName="category"
                        iconSize="sm"
                        preventDefault
                    >
                        {translate(ALL_FIELDS_GROUP_LABEL)}
                    </StyledCrmDropdownItemGroup>,
                );
            }

            allOptions.push(...nonRequiredOptions.map(renderDropdownItem));
        } else {
            allOptions.push(...filteredOptions.map(renderDropdownItem));
        }

        return allOptions;
    };

    const isNotAddFieldButton = field !== ADD_FIELD;
    const appendClass = `crm-dropdown-${exportName}-${index}`;

    const searchFilter = (term: string, { props: { text } }: { props: { text: string } }) => {
        if (!isLoading && text) {
            return text.toLowerCase().includes(term.toLowerCase());
        }
        return false;
    };

    return (
        <StyledCellContainer
            isDisabled={isLoading}
            className={appendClass}
        >
            {isNotAddFieldButton ? (
                <StyledDropdownWrapper isFocused={isFocused}>
                    <StyledDropdown
                        hasSearch
                        onToggle={handleToggle}
                        dropdownPopupHeight={CRM_EXPORT_DROPDOWN_HEIGHT}
                        width={CRM_EXPORT_DROPDOWN_WIDTH}
                        dropdownPopupPlacement={placement}
                        searchPlaceHolder={translate(CRM_CELL_PLACEHOLDER)}
                        appendTo={`.${appendClass}`}
                        onClick={onClick}
                        searchFilter={searchFilter}
                    >
                        {renderDropdownItems()}
                    </StyledDropdown>
                </StyledDropdownWrapper>
            ) : null}
        </StyledCellContainer>
    );
};
